/**
 * Native push notification bridge for the Capacitor iOS app.
 *
 * Single transport: OneSignal Cordova plugin (`onesignal-cordova-plugin`).
 * OneSignal handles the APNs (iOS) and FCM (Android) device registration
 * itself — no extra Firebase/FCM plugin needed. The backend targets devices
 * by `external_user_id`, set here via `OneSignal.login(userId)` after the
 * user authenticates.
 *
 *   - Browser/PWA: this module is a no-op. OneSignal Web SDK (loaded in
 *     index.html via window.OneSignalDeferred) handles everything.
 *   - Capacitor iOS/Android: we initialise the OneSignal native SDK and
 *     bind the logged-in user's id as the External ID.
 */

import { playPriceChime } from "./priceChime";

let _registered = false;

const ONESIGNAL_APP_ID = "df72dfb8-3f1a-43ca-a6ef-6fda4708544e";

/** @returns {boolean} true when running inside a Capacitor wrapper. */
export function isNativeApp() {
  if (typeof window === "undefined") return false;
  // Dev-only escape hatch: append `?native=1` (or `&native=1`) to any URL on
  // the preview env to manually preview the native-mode UI in a desktop
  // browser. This is a strict opt-in and has zero effect in production where
  // `window.Capacitor.isNativePlatform()` is what really matters.
  try {
    if (window.location.search.includes("native=1")) return true;
  } catch (_) {}
  return !!(
    window.Capacitor &&
    window.Capacitor.isNativePlatform &&
    window.Capacitor.isNativePlatform()
  );
}

/** Resolve the OneSignal Cordova plugin handle. The Capacitor bootstrap
 *  exposes it on either `window.plugins.OneSignal` or
 *  `window.cordova.plugins.OneSignal` depending on plugin version. */
function resolveOneSignal() {
  if (typeof window === "undefined") return null;
  return (
    window.plugins?.OneSignal ||
    window.cordova?.plugins?.OneSignal ||
    null
  );
}

/** Initialise OneSignal native push. Idempotent.
 *
 * Does NOT prompt for permission immediately — only registers the SDK.
 * The OS permission dialog is shown when the user taps the "Activer les
 * notifications" button (see `EnableNotificationsButton.jsx`). */
export async function initNativePush() {
  if (!isNativeApp() || _registered) return;

  // ⚠️ IMPORTANT — race condition fix (Android)
  // Ne PAS flipper `_registered = true` avant d'avoir vérifié que le plugin
  // OneSignal est bien exposé sur window.plugins. Sur Android/Capacitor, il
  // arrive que `deviceready` se déclenche AVANT que le pont Cordova ait fini
  // d'enregistrer ses plugins → resolveOneSignal() renvoie null. Si on flippe
  // le flag avant, les appels suivants (via setTimeout ou deviceready) sont
  // ignorés → OneSignal jamais initialisé → aucune notif reçue.
  // Solution : on RETRY silencieusement jusqu'à 10 fois (toutes les 500 ms)
  // et on ne persiste _registered qu'après un vrai OS.initialize().
  let OS = resolveOneSignal();
  if (!OS) {
    for (let i = 0; i < 10 && !OS; i++) {
      await new Promise((r) => setTimeout(r, 500));
      OS = resolveOneSignal();
    }
  }
  if (!OS) {
    // eslint-disable-next-line no-console
    console.warn("[price] OneSignal SDK never exposed on window (Cordova bridge missing)");
    return;
  }
  _registered = true;

  try {
    OS.initialize?.(ONESIGNAL_APP_ID);
    OS.Notifications?.requestPermission?.(true);

    // Intercept notification taps so we deep-link INSIDE the SPA instead
    // of letting OneSignal open a fresh WebView on the URL (which fails
    // with "page erreur" on Android when the backend sends a relative
    // path). Payload shape (OneSignal Cordova v5):
    //   event.notification.launchURL  → the full URL we set as `url`
    //   event.notification.additionalData → the `data` blob (may hold
    //                                        booking_id, ride_id, etc.)
    try {
      OS.Notifications?.addEventListener?.("click", (event) => {
        try {
          const n = event?.notification || event;
          // `data.path` (envoyé par le backend depuis `send_onesignal_push`)
          // est la source PRIORITAIRE — c'est le path relatif à router
          // dans le SPA. `launchURL` reste un fallback pour les pushes
          // legacy (ancien format `url`).
          const dataPath = n?.additionalData?.path || n?.additional_data?.path || "";
          const launchUrl = n?.launchURL || n?.launch_url || "";
          const raw = dataPath || launchUrl;
          const path = raw
            ? (() => {
                try {
                  // Si c'est une URL absolue, on extrait juste le path.
                  const u = new URL(raw);
                  return u.pathname + u.search + u.hash;
                } catch (_) {
                  return raw.startsWith("/") ? raw : "/";
                }
              })()
            : "/";
          // Client-side navigation : on préfère un event vers React Router
          // (via <PushNavigator /> dans App.js) → l'état SPA (course active,
          // AuthContext, etc.) est préservé. Fallback vers window.location
          // seulement si l'app n'a PAS de listener React monté (cold-boot
          // ou vue hors du BrowserRouter).
          if (typeof window === "undefined") return;
          // Bufferise le path — <PushNavigator /> vide ce buffer au mount
          // si l'event a été dispatché avant que la SPA soit prête.
          window.__PRICE_PENDING_PUSH_PATH__ = path;
          try {
            window.dispatchEvent(
              new CustomEvent("price:push:navigate", { detail: { path } })
            );
          } catch (_) { /* older browsers without CustomEvent */ }
          // Filet de sécurité : si personne n'a consommé le path (buffer
          // toujours plein après 900 ms), on tombe sur un hard-reload.
          setTimeout(() => {
            if (window.__PRICE_PENDING_PUSH_PATH__ === path) {
              window.__PRICE_PENDING_PUSH_PATH__ = null;
              window.location.href = path || "/";
            }
          }, 900);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[saor] onesignal click handler error:", err);
        }
      });
    } catch (_) { /* older plugin versions expose no click listener */ }

    // Foreground display listener → play the signature "Price" chime when the
    // backend push has `data.sound === "price"`. The iOS/Android OS default
    // notification sound stays disabled (server sets sound=null / silent) so
    // we can synthesize our custom chime and avoid a double-alert.
    try {
      OS.Notifications?.addEventListener?.("foregroundWillDisplay", (event) => {
        try {
          const n = event?.notification || event;
          const data = n?.additionalData || n?.additional_data || {};
          if (data && data.sound === "price") {
            playPriceChime();
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[price] onesignal foreground sound handler error:", err);
        }
      });
    } catch (_) { /* older plugin versions expose no foreground listener */ }

    // eslint-disable-next-line no-console
    console.log("[saor] OneSignal initialized appId=", ONESIGNAL_APP_ID);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[saor] OneSignal native init failed:", e);
  }
}

/** Bind the native subscription to the logged-in user. The backend targets
 *  devices through `include_external_user_ids`, so this step is required for
 *  any push to reach this device. Called from AuthContext after a successful
 *  /auth/me round-trip.
 *
 *  Race-condition safe : si OneSignal n'est pas encore initialisé (bridge
 *  Cordova pas prêt), on met le user_id en attente et on le rejouera dès
 *  que le SDK sera disponible. Sans ça, le premier login au démarrage
 *  Android échoue silencieusement → OneSignal n'associe jamais le device
 *  au user_id → 0 recipients pour tous les pushes ciblés. */
let _pendingExternalId = null;
export function nativePushLogin(externalId) {
  if (!isNativeApp() || !externalId) return;
  _pendingExternalId = String(externalId);
  const attempt = () => {
    const OS = resolveOneSignal();
    if (!OS || !OS.login) return false;
    try {
      OS.login(_pendingExternalId);
      _pendingExternalId = null;
      return true;
    } catch (_) { return false; }
  };
  if (attempt()) return;
  // Retry jusqu'à 10 fois toutes les 500 ms — couvre le cas où le bridge
  // Cordova met du temps à exposer le plugin au boot Android.
  let tries = 0;
  const t = setInterval(() => {
    tries += 1;
    if (attempt() || tries >= 10) clearInterval(t);
  }, 500);
}

/** Detach the device from any previously-logged-in user so notifications
 *  stop arriving on this device for that account. */
export async function nativePushLogout() {
  if (!isNativeApp()) return;
  try {
    const OS = resolveOneSignal();
    OS?.logout?.();
  } catch (_) {}
}

/** Backward-compatible no-op: kept so existing imports don't break. The
 *  web OneSignal SDK manages its own subscription id internally. */
export async function getSubscriptionId() {
  return null;
}
